import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HxBrandSelectComponent, HxPagedListDirective, HxProductCategorySelectComponent } from 'hx-components';
import { BrandModel, HxProductInfoService, HxUiLabelPipe, PagedList, ProductCategoryModel, ProductInfoModel, toRouterQueryParams } from 'hx-services';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { FormsModule } from '@angular/forms';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { DatePipe, NgFor } from '@angular/common';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import Timeout = NodeJS.Timeout;

interface ProductInfo extends ProductInfoModel {
  category?: ProductCategoryModel;
  brand?: BrandModel;
}

@Component({
    selector: 'hc-product-definition-list',
    templateUrl: './product-definition-list.component.html',
    styleUrls: ['./product-definition-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, HxBrandSelectComponent, FormsModule, NzInputDirective, HxProductCategorySelectComponent, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, DatePipe, HxUiLabelPipe, TranslocoPipe]
})
export class ProductDefinitionListComponent extends HxPagedListDirective<ProductInfo> implements OnInit {
  isEdit = false;

  filter: {
    categoryIds?: number[];
    brandId?: number;
    query?: string;
  } = {};
  private timeoutId?: Timeout;

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    protected productService: HxProductInfoService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
        this.filter.brandId = asNumberFn('brandId');
        this.filter.categoryIds = paramMap.get('categoryIds')?.split(',').map(str => Number(str));
        this.filter.query = paramMap.get('query') ?? undefined;
        super.load(paramMap);
      });
  }

  onBrandChanged() {
    this.filter.categoryIds = [];
    this.applyFilters();
  }

  applyFilters(delay?: number) {
    if (!this.filter.brandId) {
      this.filter.categoryIds = [];
    }
    if (delay) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => this.navigate(), delay);
    } else {
      this.navigate();
    }
  }

  private navigate() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: toRouterQueryParams(this.filter),
    });
  }

  remove(id: number): void {
    this.productService.removeProductInfo(id).subscribe(() => {
      this.loadData();
      this.notification.success(this.tr.translate('nav.ProductDefinitions'), this.tr.translate('nt.dataRemoved'));
    });
  }

  protected loadList(): Promise<PagedList<ProductInfo>> {
    return firstValueFrom(this.productService.getProductInfos({
      limit: this.limit,
      page: this.page,
      classNames: ['Brand', 'ProductCategory'],
      includeDisabled: true,
      searchText: this.filter.query,
      brandIds: this.filter.brandId ? [this.filter.brandId] : [],
      categoryIds: this.filter.categoryIds,
    })).then(paged => {
      if (paged.included) {
        const brandMap = new Map((paged.included['Brand'] as BrandModel[]).map(i => [i.id, i]));
        const categoryMap = new Map((paged.included['ProductCategory'] as ProductCategoryModel[]).map(i => [i.id, i]));
        paged.list = paged.list.map(pi => ({
          ...pi,
          category: categoryMap.get(pi.categoryId),
          brand: brandMap.get(pi.brandId)
        } as ProductInfo));
      }
      return paged;
    });
  }
}
